import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { LoadingView, NotificationContext } from "@solsys/solsys-reactjs";
import { useAuth } from "../../contexts/AuthContext";
import {
  findServerPushSubscription,
  getBrowserPushSubscription,
  getServiceWorkerRegistration,
  isPushSupported,
  parsePushSubscription,
  useUpdateSubscriptions,
} from "../../subscriptionService";

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSUa = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ can report as MacIntel while still being touch iPad.
  const iPadOs = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSUa || iPadOs;
}

/** True when running as a Home Screen / installed web app (required for iOS Web Push). */
function isStandaloneDisplayMode() {
  if (typeof window === "undefined") return false;
  const mediaStandalone =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(display-mode: standalone)").matches;
  // Legacy Safari Home Screen flag
  const iosStandalone = window.navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
}

function IOSHomeScreenInstructions() {
  return (
    <section
      style={{
        marginBottom: "28px",
        padding: "16px 18px",
        borderRadius: "8px",
        border: "1px solid var(--warning, #ef6c00)",
        background: "rgba(239, 108, 0, 0.08)",
      }}
    >
      <h3 style={{ marginTop: 0, marginBottom: "8px", color: "var(--warning, #ef6c00)" }}>
        One more step on iPhone / iPad
      </h3>
      <p style={{ marginTop: 0, marginBottom: "12px", lineHeight: 1.5 }}>
        Notifications only work when you open E-Crane from a Home Screen icon — not from a Safari tab.
      </p>
      <ol style={{ margin: 0, paddingLeft: "1.2em", lineHeight: 1.7 }}>
        <li>In <strong>Safari</strong>, tap the <strong>Share</strong> button (square with ↑).</li>
        <li>Tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.</li>
        <li>Open E-Crane from that new icon, then come back here.</li>
      </ol>
    </section>
  );
}

export default function PushNotificationsSettings() {
  const { user } = useAuth();
  const client = useApolloClient();
  const { addNotification } = useContext(NotificationContext);
  const { ensurePushSubscription } = useUpdateSubscriptions();
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [browserSubscription, setBrowserSubscription] = useState(null);
  const [serverRow, setServerRow] = useState(null);

  const iosDevice = useMemo(() => isIOSDevice(), []);
  const standalone = useMemo(() => isStandaloneDisplayMode(), []);
  const needsHomeScreenInstall = iosDevice && !standalone;

  const refreshStatus = useCallback(async ({ showFullPageLoader = false } = {}) => {
    if (showFullPageLoader) setLoading(true);
    try {
      const pushSupported = isPushSupported();
      setSupported(pushSupported);
      setPermission(typeof Notification !== "undefined" ? Notification.permission : "default");

      if (!pushSupported) {
        setBrowserSubscription(null);
        setServerRow(null);
        return;
      }

      const registration = await getServiceWorkerRegistration();
      const browserSub = await getBrowserPushSubscription(registration);
      const parsed = parsePushSubscription(browserSub);
      setBrowserSubscription(parsed);

      if (parsed?.endpoint && user?._id) {
        const rows = await findServerPushSubscription(client, {
          endpoint: parsed.endpoint,
          userId: user._id,
        });
        setServerRow(rows[0] ?? null);
      } else {
        setServerRow(null);
      }
    } catch (err) {
      console.error(err);
      addNotificationRef.current?.("bad", err.message || "Failed to check notification status");
    } finally {
      setLoading(false);
    }
  }, [client, user?._id]);

  useEffect(() => {
    refreshStatus({ showFullPageLoader: true });
  }, [refreshStatus]);

  const handleEnable = async ({ forceFresh }) => {
    if (!user?._id) return;
    setBusy(true);
    try {
      const result = await ensurePushSubscription(user._id, {
        forceFresh,
        requestPermission: true,
      });

      if (!result.ok) {
        const messages = {
          unsupported: "This device or browser cannot receive notifications.",
          denied: "Notifications are blocked. Turn them on in your device settings, then try again.",
          permission_default: "Please allow notifications when your device asks.",
          subscribe_failed: "Could not turn on notifications. Please try again.",
          no_user: "You must be signed in.",
          error: "Could not turn on notifications. Please try again.",
          missing_fields: "Could not turn on notifications. Please try again.",
        };
        addNotification("bad", messages[result.reason] || "Could not turn on notifications.");
      } else {
        addNotification(
          "good",
          forceFresh ? "Notifications were reset for this device." : "Notifications are on for this device."
        );
      }
      await refreshStatus();
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingView />;

  const registeredInSystem = Boolean(serverRow);
  const browserReady = Boolean(browserSubscription?.endpoint);
  const permissionGranted = permission === "granted";
  const isReady = supported && !needsHomeScreenInstall && permissionGranted && browserReady && registeredInSystem;
  const actionDisabled = busy || !supported || permission === "denied" || needsHomeScreenInstall;

  let statusTitle = "Notifications are not set up yet";
  let statusBody = "Tap the button below to turn on notifications for this device.";
  let statusColor = "var(--warning, #ef6c00)";
  let statusBackground = "rgba(239, 108, 0, 0.08)";
  let statusBorder = "var(--warning, #ef6c00)";

  if (needsHomeScreenInstall) {
    statusTitle = "Open E-Crane from your Home Screen";
    statusBody = "Follow the steps below, then you can turn on notifications.";
    statusColor = "var(--warning, #ef6c00)";
  } else if (!supported) {
    statusTitle = "Notifications are not available here";
    statusBody = "Try opening E-Crane in Safari on iPhone, or Chrome/Edge on a computer.";
    statusColor = "var(--danger, #c62828)";
    statusBackground = "rgba(198, 40, 40, 0.06)";
    statusBorder = "var(--danger, #c62828)";
  } else if (permission === "denied") {
    statusTitle = "Notifications are blocked";
    statusBody = iosDevice
      ? "On your iPhone: Settings → Notifications → find E-Crane (or Safari) → Allow Notifications. Then return here and try again."
      : "Allow notifications for this site in your browser settings, then try again.";
    statusColor = "var(--danger, #c62828)";
    statusBackground = "rgba(198, 40, 40, 0.06)";
    statusBorder = "var(--danger, #c62828)";
  } else if (isReady) {
    statusTitle = "Notifications are on";
    statusBody = "This device should receive alerts for tickets, chats, and assignments.";
    statusColor = "var(--success, #2e7d32)";
    statusBackground = "rgba(46, 125, 50, 0.08)";
    statusBorder = "var(--success, #2e7d32)";
  }

  return (
    <div className="push-notifications-settings" style={{ padding: "24px", maxWidth: "560px" }}>
      <h2 style={{ marginTop: 0, marginBottom: "8px" }}>Notifications</h2>
      <p style={{ color: "var(--text-muted, #666)", marginTop: 0, marginBottom: "24px", lineHeight: 1.5 }}>
        Make sure this phone or computer can receive E-Crane alerts.
      </p>

      <section
        style={{
          marginBottom: "24px",
          padding: "18px 20px",
          borderRadius: "8px",
          border: `1px solid ${statusBorder}`,
          background: statusBackground,
        }}
      >
        <div style={{ fontSize: "20px", fontWeight: 700, color: statusColor, marginBottom: "6px" }}>
          {statusTitle}
        </div>
        <p style={{ margin: 0, lineHeight: 1.5 }}>{statusBody}</p>
      </section>

      {needsHomeScreenInstall && <IOSHomeScreenInstructions />}

      {!needsHomeScreenInstall && !isReady && permission !== "denied" && supported && (
        <button
          type="button"
          disabled={actionDisabled}
          onClick={() => handleEnable({ forceFresh: false })}
          style={{
            display: "block",
            width: "100%",
            maxWidth: "360px",
            padding: "14px 20px",
            fontWeight: "bold",
            fontSize: "16px",
            marginBottom: "16px",
          }}
        >
          {busy ? "Working…" : "Turn on notifications"}
        </button>
      )}

      {isReady && (
        <div style={{ marginBottom: "16px" }}>
          <p style={{ marginTop: 0, marginBottom: "10px", color: "var(--text-muted, #666)", lineHeight: 1.5 }}>
            Not getting alerts? Tap below to reset notifications on this device.
          </p>
          <button
            type="button"
            disabled={actionDisabled}
            onClick={() => handleEnable({ forceFresh: true })}
            style={{ padding: "12px 18px" }}
          >
            {busy ? "Working…" : "Fix notifications"}
          </button>
        </div>
      )}

      {!isReady && !needsHomeScreenInstall && permission === "denied" && (
        <button
          type="button"
          disabled={busy}
          onClick={() => refreshStatus()}
          style={{ padding: "12px 18px", marginBottom: "16px" }}
        >
          I turned them on — check again
        </button>
      )}

      <details
        open={showDetails}
        onToggle={(e) => setShowDetails(e.target.open)}
        style={{ marginTop: "28px", color: "var(--text-muted, #666)" }}
      >
        <summary style={{ cursor: "pointer", userSelect: "none" }}>Advanced details</summary>
        <div style={{ marginTop: "12px", fontSize: "14px", lineHeight: 1.6 }}>
          <div>Support: {supported ? "Yes" : "No"}</div>
          {iosDevice && <div>Home Screen: {standalone ? "Yes" : "No"}</div>}
          <div>Permission: {permission}</div>
          <div>Device subscription: {browserReady ? "Yes" : "No"}</div>
          <div>Saved in E-Crane: {registeredInSystem ? "Yes" : "No"}</div>
          <button
            type="button"
            disabled={busy}
            onClick={() => refreshStatus()}
            style={{ marginTop: "12px", padding: "8px 14px" }}
          >
            Refresh
          </button>
        </div>
      </details>
    </div>
  );
}
